import { GroupMemberRole } from "@/app/api/types/group";
import type { GroupMemberResponse, GroupResponse } from "@/app/api/types/group";

/**
 * Whether a membership `role` administers its group.
 *
 * `ADMIN_AND_HR` (the agency-manager role) is a strict SUPERSET of `ADMIN`, so
 * both qualify. This mirrors the backend's `get_group_admin` dependency, which
 * admits exactly these two roles. Every "is this member a group admin?" check
 * MUST route through here: comparing against a bare `ADMIN` (or the raw `0`)
 * silently locks agency managers out of surfaces they are authorized for, which
 * is precisely how the group AI-context editor regressed (STK — group context
 * was read-only for ADMIN_AND_HR members).
 */
export function isGroupAdminRole(
  role: GroupMemberResponse["role"] | undefined,
): boolean {
  return (
    role === GroupMemberRole.ADMIN || role === GroupMemberRole.ADMIN_AND_HR
  );
}

/**
 * Whether a membership `role` may edit the group's AI context.
 *
 * Admin roles qualify, plus `CONTEXT_EDITOR` — a member whose ONE extra grant
 * is this field (it confers no other admin power, so it must never feed
 * `isGroupAdminRole`). Mirrors the V2 PATCH auth matrix in
 * `apps/api/src/domains/core/group/auth.ts`.
 */
export function canEditGroupContextRole(
  role: GroupMemberResponse["role"] | undefined,
): boolean {
  return isGroupAdminRole(role) || role === GroupMemberRole.CONTEXT_EDITOR;
}

/**
 * Whether `userId` administers at least one of `groups`.
 *
 * A user administers a group when they are its `owner_id` OR hold an admin
 * membership role — mirroring the backend `is_group_admin` predicate, which
 * admits the group owner directly (ownership can be transferred to a user who
 * is only a plain MEMBER of the group, so the role check alone is not enough).
 *
 * Company owners are NOT considered here — they already reach the group
 * workspace through the owner-only "Company" entry, so callers that need "can
 * see groups at all" must OR this with the ownership check.
 */
export function isGroupAdmin(
  groups: GroupResponse[] | undefined,
  userId: string | undefined,
): boolean {
  if (!userId) return false;
  return (groups ?? []).some(
    (group) =>
      group.owner_id === userId ||
      group.members.some(
        (member) => member.user_id === userId && isGroupAdminRole(member.role),
      ),
  );
}
